import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, MonitorSmartphone, Ticket, Trophy, Crown, Gift,
  Bell, Download, LogOut, Menu, X, ArrowLeft, ShieldCheck, Gauge, MessagesSquare, Star,
  Code2, Flag, FileText, QrCode, Signature, Radio, ScrollText, Car,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import logo from "@/assets/logo.png";

const adminNav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/stations", label: "Stations", icon: MonitorSmartphone },
  { to: "/admin/stations-live", label: "Live Stations", icon: Radio },
  { to: "/admin/bookings", label: "Bookings", icon: Ticket },
  { to: "/admin/sim-sessions", label: "Sim Sessions", icon: Gauge },
  { to: "/admin/waiver", label: "Waiver", icon: FileText, adminOnly: true },
  { to: "/admin/waiver/signatures", label: "Signatures", icon: Signature, adminOnly: true },
  { to: "/admin/qr-codes", label: "QR Codes", icon: QrCode, adminOnly: true },
  { to: "/admin/leaderboard-tracks", label: "Track Allowlist", icon: Flag },
  { to: "/admin/gift-card-requests", label: "Gift-card Requests", icon: Gift, adminOnly: true },
  { to: "/admin/tournaments", label: "Tournaments", icon: Trophy },
  { to: "/admin/car-clubs", label: "Car Clubs", icon: Car },
  { to: "/admin/memberships", label: "Memberships", icon: Crown },
  { to: "/admin/rewards", label: "Rewards", icon: Gift },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/embeds", label: "Embeds", icon: Code2 },
  { to: "/admin/simhub", label: "SimHub", icon: Gauge },
  { to: "/admin/channels", label: "Channels", icon: MessagesSquare },
  { to: "/admin/notifications", label: "Notifications", icon: Bell },
  { to: "/admin/data-export", label: "Data Export", icon: Download, adminOnly: true },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText, adminOnly: true },
];

const NavList = ({ onNavigate }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const items = adminNav.filter((i) => isAdmin || !i.adminOnly);
  return (
  <nav className="flex flex-col gap-1.5">
    {items.map(({ to, label, icon: Icon, end }) => (
      <NavLink key={to} to={to} end={end} onClick={onNavigate}
        data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 rounded-xl font-semibold tracking-wide transition-all ${
            isActive ? "text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
          }`}
        style={({ isActive }) => isActive
          ? { background: "linear-gradient(90deg, rgba(255,46,151,0.16), rgba(139,92,246,0.12))", border: "1px solid rgba(255,46,151,0.35)" }
          : { border: "1px solid transparent" }}>
        <Icon className="w-5 h-5 shrink-0" /> <span>{label}</span>
      </NavLink>
    ))}
  </nav>
  );
};

export const AdminLayout = ({ children }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const Inner = ({ onNavigate }) => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-2 py-4 mb-1">
        <img src={logo} alt="Pit Lane" className="h-10 w-auto object-contain" />
      </div>
      <div className="chip mx-2 mb-4" style={{ background: "rgba(255,46,151,0.14)", border: "1px solid rgba(255,46,151,0.4)", color: "#ff2e97" }}>
        <ShieldCheck className="w-3.5 h-3.5" /> Admin Console
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin px-1"><NavList onNavigate={onNavigate} /></div>
      <div className="mt-4 pt-4 border-t border-white/10 px-1 space-y-1.5">
        <button onClick={() => navigate("/dashboard")}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-slate-400 hover:text-white hover:bg-white/5 transition-all"
          data-testid="admin-back-to-app">
          <ArrowLeft className="w-5 h-5" /> Back to App
        </button>
        <button onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold text-slate-400 hover:text-white hover:bg-red-500/10 transition-all"
          data-testid="admin-logout">
          <LogOut className="w-5 h-5" /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen grid-overlay">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col glass border-r border-white/10 px-4 py-3 z-30">
        <Inner />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 max-w-[80%] glass border-r border-white/10 px-4 py-3">
            <button className="absolute top-4 right-4 text-slate-400 hover:text-white" onClick={() => setMobileOpen(false)}>
              <X className="w-6 h-6" />
            </button>
            <Inner onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 glass border-b border-white/10 lg:hidden">
          <div className="flex items-center gap-3 px-4 h-16">
            <button className="text-slate-300 hover:text-white" onClick={() => setMobileOpen(true)} data-testid="admin-open-menu">
              <Menu className="w-6 h-6" />
            </button>
            <span className="font-display font-bold text-white">Admin Console</span>
          </div>
        </header>
        <main className="px-4 sm:px-6 lg:px-10 py-8 max-w-7xl mx-auto w-full">{children}</main>
      </div>
    </div>
  );
};
